import { fmtDate, whenText } from '@jt/core';
import type { ApplicationListItem } from '@jt/shared';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef } from 'react';
import { useTodayDate } from '../api/hooks';
import { useUI } from './overlays';
import { Logo, SourceTag, StagePill } from './ui';

const VIRTUAL_FROM = 200;
const ROW_H = 57;

function Row({ a, selected, today }: { a: ApplicationListItem; selected: boolean; today: string }) {
  const ui = useUI();
  const c = a.contacts[0];
  return (
    <tr
      className={selected ? 'sel' : ''}
      tabIndex={0}
      onClick={() => ui.openApp(a.id)}
      onKeyDown={(e) => e.key === 'Enter' && ui.openApp(a.id)}
    >
      <td>
        <div className="tc">
          <Logo name={a.company} size={30} />
          <div>
            <b>{a.role || '—'}</b>
            <span>{a.company || '—'}</span>
          </div>
        </div>
      </td>
      <td>
        <StagePill stage={a.stage} />
      </td>
      <td>
        <SourceTag source={a.source} />
      </td>
      <td>{a.salaryRange || '—'}</td>
      <td>{c?.name || '—'}</td>
      <td>
        {a.nextDate ? whenText(a.nextDate, a.nextTime, today) + (a.nextStep ? ` · ${a.nextStep}` : '') : '—'}
      </td>
      <td>{fmtDate(a.lastActivityOn)}</td>
    </tr>
  );
}

/** Таблица вакансий; при > 200 строк рендерятся только видимые. */
export function AppTable({ list, selectedId }: { list: ApplicationListItem[]; selectedId: string | null }) {
  const today = useTodayDate();
  const ref = useRef<HTMLDivElement>(null);
  const virtual = list.length > VIRTUAL_FROM;
  const v = useVirtualizer({
    count: list.length,
    getScrollElement: () => ref.current,
    estimateSize: () => ROW_H,
    overscan: 12,
    enabled: virtual,
  });
  const items = virtual ? v.getVirtualItems() : [];
  const top = virtual && items.length ? items[0]!.start : 0;
  const bottom = virtual && items.length ? v.getTotalSize() - items[items.length - 1]!.end : 0;
  const rows = virtual ? items.map((i) => list[i.index]!) : list;
  return (
    <div className={`tblw ${virtual ? 'vt' : ''}`} id="tblw" ref={ref}>
      <table className="tbl">
        <thead>
          <tr>
            <th>Вакансия</th>
            <th>Статус</th>
            <th>Источник</th>
            <th>Зарплата</th>
            <th>Контакт</th>
            <th>Следующий шаг</th>
            <th>Активность</th>
          </tr>
        </thead>
        <tbody>
          {top > 0 && (
            <tr aria-hidden="true">
              <td colSpan={7} style={{ height: top, padding: 0, border: 0 }} />
            </tr>
          )}
          {rows.map((a) => (
            <Row key={a.id} a={a} selected={selectedId === a.id} today={today} />
          ))}
          {bottom > 0 && (
            <tr aria-hidden="true">
              <td colSpan={7} style={{ height: bottom, padding: 0, border: 0 }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
